/**
 * Checkbox — Figma component `Input/Checkbox` (instance 12:408).
 *
 * ASSET NOTE (design-to-code G5). Figma exports the 16×16 box as an image asset.
 * It is NOT used, for two reasons, and this is the record of that choice:
 *   1. the exported asset URL expires in ~7 days, so committing it would ship a
 *      link that breaks;
 *   2. the glyph is a plain checked box, which lucide's `Check` matches exactly,
 *      and lucide-react is this project's declared icon source (Part B stack).
 * The geometry is preserved: 16×16 outer box, `radius` 4, 8px gap to the label,
 * label `Body/14` in `text/primary` — all measured off the node.
 *
 * A real `<input type="checkbox">` carries the semantics; it is visually hidden
 * (not `display:none`) so it stays focusable and announced.
 */

import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes } from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> {
  label: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, className, checked, ...rest },
  ref,
) {
  const id = useId();
  return (
    <div className={cn('flex h-5 items-center gap-2', className)}>
      <span className="relative inline-flex size-4 shrink-0">
        <input
          ref={ref}
          id={id}
          type="checkbox"
          checked={checked}
          className="peer absolute inset-0 size-4 cursor-pointer appearance-none rounded-[4px] border border-line-strong bg-surface checked:border-indigo checked:bg-indigo"
          {...rest}
        />
        <Check
          aria-hidden="true"
          strokeWidth={3}
          className="pointer-events-none absolute inset-0 m-auto size-3 text-fg-inverse opacity-0 peer-checked:opacity-100"
        />
      </span>
      <label htmlFor={id} className="cursor-pointer text-body text-fg">
        {label}
      </label>
    </div>
  );
});
